import React, { useState } from "react";
import {
  Box,
  Grid,
  Divider,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  EditOutlined as EditIcon,
  AddOutlined as AddIcon,
  DeleteOutlined as DeleteIcon
} from "@mui/icons-material";
import clsx from "clsx";
import agent from "../../../../../agent/agent";
import {
  Notification,
  notificationTypes
} from "../../../../../common/Notification";

// Функция для форматирования даты из ISO формата или YYYY-MM-DD в DD.MM.YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    // Обрабатываем ISO формат (например: "2026-01-01T00:00:00.000000Z")
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // Если не удалось распарсить как Date, пробуем простое разбиение
      const datePart = dateString.split('T')[0]; // Берем только часть до T
      const [year, month, day] = datePart.split('-');
      if (year && month && day) {
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
      }
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    // Fallback: пробуем простое разбиение
    const datePart = dateString.split('T')[0] || dateString;
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }
    return '';
  }
};

// Функция для обработки ошибок API
const handleApiError = (error) => {
  let errorMessage = 'Произошла ошибка при выполнении операции';

  if (error?.response?.data) {
    const errorData = error.response.data;

    // Если есть основное сообщение
    if (errorData.message) {
      errorMessage = errorData.message;
    }
    // Если есть объект errors с полями
    else if (errorData.errors) {
      const errorMessages = [];
      Object.keys(errorData.errors).forEach(key => {
        const fieldErrors = errorData.errors[key];
        if (Array.isArray(fieldErrors)) {
          errorMessages.push(...fieldErrors);
        } else {
          errorMessages.push(fieldErrors);
        }
      });
      if (errorMessages.length > 0) {
        errorMessage = errorMessages.join('; ');
      }
    }
  } else if (error?.message) {
    errorMessage = error.message;
  }

  Notification({
    message: errorMessage,
    type: notificationTypes.error
  });
};

// Функция для преобразования ISO даты в формат YYYY-MM-DD для поля date input
const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    // Если уже в формате YYYY-MM-DD, возвращаем как есть
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Обрабатываем ISO формат
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    // Fallback: берем часть до T
    const datePart = dateString.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
    return '';
  } catch (error) {
    return '';
  }
};

// Функция для проверки, является ли дата активной (прошла или сегодня)
const isDateActive = (dateString) => {
  if (!dateString) return true; // Если даты нет, считаем активной
  try {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date <= today;
  } catch (error) {
    return true;
  }
};

// Функция для группировки элементов по уровням
const groupItemsByLevel = (items) => {
  const grouped = {};

  if (!items || !Array.isArray(items)) {
    return grouped;
  }

  items.forEach(item => {
    // Проверяем, что item существует и имеет level
    if (!item || item.level === undefined || item.level === null) {
      return;
    }

    // Преобразуем level в строку для консистентности
    const level = String(item.level);
    if (!grouped[level]) {
      grouped[level] = [];
    }
    grouped[level].push(item);
  });

  // Сортируем записи внутри каждого уровня по дате effective_from
  Object.keys(grouped).forEach(level => {
    grouped[level].sort((a, b) => {
      const dateA = a.effective_from ? new Date(a.effective_from) : new Date(0);
      const dateB = b.effective_from ? new Date(b.effective_from) : new Date(0);
      return dateA - dateB;
    });
  });

  return grouped;
};

const ItemSettingLevel = ({title, type}) => {
  const classes = useStyles();

  const [items, setItems] = React.useState([]);
  const getItems = async () => {
    const res = await agent.get(`/mechanic-statistics/level-grades?type=${ type }`).then(res => res.data || []).catch(() => []);
    setItems(res);
  };
  React.useEffect(() => {
    (async () => {
      await getItems();
    })();
  }, []);

  const groupedItems = React.useMemo(() => {
    return groupItemsByLevel(items || []);
  }, [items]);

  return (
    <Box className={classes.root}>
      <Typography className={classes.title}>{title}</Typography>
      <Grid container>
        {Object.keys(groupedItems).sort((a, b) => parseInt(a) - parseInt(b)).map((level) => {
          const levelItems = groupedItems[level];
          return (
            <React.Fragment key={level}>
              <Grid item xs={12}><Divider sx={{color: "#F3F3F3"}}/></Grid>
              <Grid item xs={12}>
                <LevelGroup
                  level={parseInt(level)}
                  items={levelItems}
                  type={type}
                  onUpdate={getItems}
                />
              </Grid>
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
};
const LevelGroup = ({ level, items, type, onUpdate }) => {
  const classes = useStyles();

  // Определяем активную и следующие записи
  // Активная - последняя запись с датой <= сегодня (или без даты)
  const activeItems = items.filter(item => !item.effective_from || isDateActive(item.effective_from));
  const activeItem = activeItems.length > 0
    ? activeItems.reduce((latest, current) => {
        const currentDate = current.effective_from ? new Date(current.effective_from) : new Date(0);
        const latestDate = latest.effective_from ? new Date(latest.effective_from) : new Date(0);
        return currentDate > latestDate ? current : latest;
      })
    : items[0];

  // Следующие записи - все записи с датой > сегодня, отсортированные по дате
  const nextItems = items
    .filter(item => {
      // Исключаем активную запись и берем только будущие
      return item.id !== activeItem.id && 
             item.effective_from && 
             !isDateActive(item.effective_from);
    })
    .sort((a, b) => {
      // Сортируем по дате effective_from (от ближайшей к дальнейшей)
      const dateA = new Date(a.effective_from);
      const dateB = new Date(b.effective_from);
      return dateA - dateB;
    });

  return (
    <GridRowItem
      level={level}
      norm={activeItem.hours_threshold}
      price={activeItem.hour_cost}
      item={activeItem}
      type={type}
      onUpdate={onUpdate}
      nextItems={nextItems}
      isActive={true}
      showLevelHeader={false}
    />
  );
};

const GridRowItem = ({ level, norm, price, item, type, onUpdate, isActive = true, showLevelHeader = true, nextItems, isChildren = false }) => {
  const classes = useStyles();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [hoursThreshold, setHoursThreshold] = useState(norm || '');
  const [hourCost, setHourCost] = useState(price || '');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [addHoursThreshold, setAddHoursThreshold] = useState('');
  const [addHourCost, setAddHourCost] = useState('');
  const [addEffectiveFrom, setAddEffectiveFrom] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenEditDialog = () => {
    setHoursThreshold(norm || '');
    setHourCost(price || '');
    setEffectiveFrom(formatDateForInput(item.effective_from) || '');
    setOpenEditDialog(true);
  }
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEffectiveFrom('');
  }
  const handleOpenAddDialog = () => {
    setAddHoursThreshold('');
    setAddHourCost('');
    setAddEffectiveFrom('');
    setOpenAddDialog(true);
  }
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setAddHoursThreshold('');
    setAddHourCost('');
    setAddEffectiveFrom('');
  }
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const payload = {
        hours_threshold: parseFloat(hoursThreshold) || 0,
        hour_cost: parseFloat(hourCost) || 0,
        level: item.level,
        type: type
      };

      // Добавляем effective_from только если поле заполнено
      if (effectiveFrom) {
        payload.effective_from = effectiveFrom;
      }

      await agent.put(`/mechanic-statistics/level-grades/${item.id}`, payload);
      if (onUpdate) {
        await onUpdate();
      }
      setOpenEditDialog(false);
      Notification({
        message: 'Запись успешно обновлена',
        type: notificationTypes.success
      });
    } catch (error) {
      console.error('Error updating level grade:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }
  const handleAdd = async () => {
    setIsLoading(true);
    try {
      const payload = {
        hours_threshold: parseFloat(addHoursThreshold) || 0,
        hour_cost: parseFloat(addHourCost) || 0,
        level: item.level,
        type: type
      };

      // Добавляем effective_from только если поле заполнено
      if (addEffectiveFrom) {
        payload.effective_from = addEffectiveFrom;
      }

      await agent.post(`/mechanic-statistics/level-grades`, payload);
      if (onUpdate) {
        await onUpdate();
      }
      setOpenAddDialog(false);
      Notification({
        message: 'Запись успешно добавлена',
        type: notificationTypes.success
      });
    } catch (error) {
      console.error('Error adding level grade:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }
  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  }
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  }
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await agent.delete(`/mechanic-statistics/level-grades/${item.id}`);
      if (onUpdate) {
        await onUpdate();
      }
      setOpenDeleteDialog(false);
      Notification({
        message: 'Запись успешно удалена',
        type: notificationTypes.success
      });
    } catch (error) {
      console.error('Error deleting level grade:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Grid container wrap="nowrap" className={clsx(classes.gridRowItem, {
        [classes.gridRowItemActive]: isActive && !isChildren,
        [classes.gridRowItemNext]: isChildren
      })}>
        <Grid item xs={3}>
          {Boolean(!isChildren) && (
            <div className={clsx({
              [classes.levelItem]: true,
              [`level-${level}`]: true
            })}>
              <img src={require('../../../../../assets/svg/statistics/user-level.svg').default}/>
              <span>Уровень {level}</span>
            </div>
          )}
        </Grid>
        <Grid item sx={{margin: "0 20px"}}><Divider orientation="vertical" sx={{color: "#F3F3F3", margin: "-10px 0", height: "calc(100% + 20px)"}}/></Grid>
        <Grid item xs={3}>
          <div>
            <span className={clsx(classes.gridRowItemTextValue, {
              [classes.gridRowItemTextValueNext]: isChildren
            })}>{level === 1 ? '<' : '>'} {norm} н/ч</span>
            {item.effective_from && (<div className={clsx(classes.effectiveDate, {
              [classes.effectiveDateNext]: isChildren
            })}>
              Действует с {formatDate(item.effective_from)}
            </div>)}
          </div>
        </Grid>
        <Grid item sx={{margin: "0 20px"}}><Divider orientation="vertical" sx={{color: "#F3F3F3", margin: "-10px 0", height: "calc(100% + 20px)"}}/></Grid>
        <Grid item xs={3}>
          <div>
            <span className={clsx(classes.gridRowItemTextValue, {
              [classes.gridRowItemTextValueNext]: isChildren
            })}>{price || '0'} руб.</span>
            {item.effective_from && (<div className={clsx(classes.effectiveDate, {
              [classes.effectiveDateNext]: isChildren
            })}>
              Действует с {formatDate(item.effective_from)}
            </div>)}
          </div>
        </Grid>
        <Grid item sx={{margin: "0 20px"}}><Divider orientation="vertical" sx={{color: "#F3F3F3", margin: "-10px 0", height: "calc(100% + 20px)"}}/></Grid>
        <Grid item xs="auto">
          <Grid container spacing={0.5} alignItems="center">
            <Grid item>
              <IconButton className={classes.buttonEditItem} onClick={handleOpenEditDialog}>
                <EditIcon/>
              </IconButton>
            </Grid>
            {Boolean(!isChildren) && (
              <Grid item>
                <IconButton className={classes.buttonAddItem} onClick={handleOpenAddDialog}>
                  <AddIcon/>
                </IconButton>
              </Grid>
            )}
            <Grid item>
              <IconButton className={classes.buttonDeleteItem} onClick={handleOpenDeleteDialog}>
                <DeleteIcon/>
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Следующие записи (дочерние элементы) */}
      {!isChildren && Boolean((nextItems || []).length > 0) && (
        <Box sx={{ mt: 1 }}>
          {nextItems.map((nextItem, index) => (
            <Box key={nextItem.id || index} sx={{ mb: index < nextItems.length - 1 ? 1 : 0 }}>
              <Divider sx={{ color: "#E0E0E0", mb: 1 }} />
              <GridRowItem
                isChildren={true}
                type={type}
                item={nextItem}
                norm={nextItem.hours_threshold}
                level={nextItem.level}
                price={nextItem.hour_cost}
                onUpdate={onUpdate}
                isActive={false}
                showLevelHeader={false}
              />
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={openEditDialog} onClose={isLoading ? undefined : handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Редактирование уровня {level}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Порог часов (н/ч)"
                type="number"
                value={hoursThreshold}
                onChange={(e) => setHoursThreshold(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Стоимость часа (руб.)"
                type="number"
                value={hourCost}
                onChange={(e) => setHourCost(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Начинать действовать с"
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isLoading}>Отмена</Button>
          <Button onClick={handleSave} variant="contained" disabled={isLoading}>Сохранить</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openAddDialog} onClose={isLoading ? undefined : handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Добавление записи для уровня {level}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Порог часов (н/ч)"
                type="number"
                value={addHoursThreshold}
                onChange={(e) => setAddHoursThreshold(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Стоимость часа (руб.)"
                type="number"
                value={addHourCost}
                onChange={(e) => setAddHourCost(e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Начинать действовать с"
                type="date"
                value={addEffectiveFrom}
                onChange={(e) => setAddEffectiveFrom(e.target.value)}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isLoading}>Отмена</Button>
          <Button onClick={handleAdd} variant="contained" disabled={isLoading}>Добавить</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteDialog} onClose={isLoading ? undefined : handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Вы уверены, что хотите удалить запись для уровня {level}?
            {item.effective_from && (
              <div style={{ marginTop: 8, fontSize: 14, opacity: 0.7 }}>
                Действует с {formatDate(item.effective_from)}
              </div>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isLoading}>Отмена</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={isLoading}>Удалить</Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  )
}

const useStyles = makeStyles(() => ({
  root: {
    background: "#fff",
    borderRadius: 20,
    padding: "20px 30px",
    boxSizing: "border-box",
  },
  title: {
    marginBottom: 10,
    fontSize: 24,
    fontWeight: 700,
    color: "#434146",
    lineHeight: "29px",
    letterSpacing: "0.03em",
  },
  gridRowItem: {
    padding: "10px 0",
    boxSizing: "border-box",
  },
  gridRowItemActive: {
    opacity: 1,
  },
  gridRowItemNext: {
    // Стили для дочерних элементов через цвет текста
  },
  gridRowItemTextValue: {
    fontSize: 16,
    lineHeight: 1.2,
    color: "#FF3C43"
  },
  gridRowItemTextValueNext: {
    color: "#999999",
  },
  effectiveDate: {
    fontSize: 12,
    lineHeight: 1.2,
    color: "#434146",
    marginTop: 4,
    opacity: 0.7
  },
  effectiveDateNext: {
    color: "#999999",
    opacity: 0.6,
  },

  levelItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: 24,
    flexWrap: "wrap",

    "& img": {
      width: "20px",
      height: "20px",
      objectFit: "contain",
      objectPosition: "center",
    },
    "& span": {
      fontSize: 16,
      lineHeight: 1.2,
      color: "#434146",
    },

    "&.level-1 img": {
      filter: "invert(82%) sepia(7%) saturate(2773%) hue-rotate(331deg) brightness(91%) contrast(85%)",
    },
    "&.level-2 img": {
      filter: "invert(75%) sepia(37%) saturate(14%) hue-rotate(344deg) brightness(98%) contrast(92%)",
    },
    "&.level-3 img": {
      filter: "invert(96%) sepia(98%) saturate(1585%) hue-rotate(316deg) brightness(97%) contrast(109%)",
    },
  },
  buttonEditItem: {
    padding: 1,
    width: 24, height: 24,
    borderRadius: "4px",
    border: "1px solid #C4C4C4",

    "& svg": {
      width: "100%",
      height: "100%",
      color: "#C4C4C4"
    }
  },
  buttonAddItem: {
    padding: 1,
    width: 24, height: 24,
    borderRadius: "4px",
    border: "1px solid #C4C4C4",

    "& svg": {
      width: "100%",
      height: "100%",
      color: "#C4C4C4"
    }
  },
  buttonDeleteItem: {
    padding: 1,
    width: 24, height: 24,
    borderRadius: "4px",
    border: "1px solid #C4C4C4",

    "& svg": {
      width: "100%",
      height: "100%",
      color: "#C4C4C4"
    }
  }
}));

export default ItemSettingLevel;
